// zr-core/callQueue — the CLIENT-SIDE model of the Connectra+ digital-contact-centre call queue.
//
// The AUTHORITATIVE queue is a Redis Stream (name = room_id) with an operator consumer-group, owned
// by the edge media backend (edge-devs.soa.africa). This module is the pure, framework-agnostic
// VIEW the SDK maintains from queue events so the UI can render "you're #3 in line", show operator
// availability, and route a client to the next available operator. No I/O, fully unit-testable.

import type { AvailabilityStatus } from './product';

/** A client waiting in the queue (a publisher to the room_id stream). */
export interface QueuedClient {
  clientId: string;
  enqueuedAt: number;
  displayName?: string;
  /** Why they're queued, e.g. "all operators busy". */
  reason?: string;
}

/** An operator/agent (consumer-group member) and their live state. */
export interface OperatorState {
  operatorId: string;
  availability: AvailabilityStatus;
  /** The clientId this operator is currently talking to, if any. */
  activeCallWith?: string;
  displayName?: string;
}

/** A "call me back" request a client schedules for a later in-hours slot. */
export interface CallBackRequest {
  clientId: string;
  /** Epoch ms the client asked to be called back. */
  requestedAt: number;
  /** Epoch ms of the desired callback slot (within business hours — the app validates). */
  slotAt: number;
  displayName?: string;
  note?: string;
}

export interface CallQueueSnapshot {
  roomId: string;
  waiting: QueuedClient[];
  operators: OperatorState[];
  availableOperators: number;
  busyOperators: number;
}

/**
 * The client-side call-queue projection. Fed by queue events, it answers the two questions a
 * Connectra+ UI needs: "where am I in line?" (client) and "who's the next client to serve?" (operator).
 */
export class CallQueue {
  private waiting: QueuedClient[] = [];
  private operators = new Map<string, OperatorState>();

  constructor(public readonly roomId: string) {}

  /** Add a client to the back of the queue (idempotent by clientId). Returns their 1-based position. */
  enqueue(client: QueuedClient): number {
    if (!this.waiting.some((c) => c.clientId === client.clientId)) this.waiting.push(client);
    return this.positionOf(client.clientId);
  }

  /** Remove a client (they got connected, cancelled, or left). */
  removeClient(clientId: string): void {
    this.waiting = this.waiting.filter((c) => c.clientId !== clientId);
  }

  /** 1-based position in the queue, or 0 if not queued. */
  positionOf(clientId: string): number {
    const i = this.waiting.findIndex((c) => c.clientId === clientId);
    return i < 0 ? 0 : i + 1;
  }

  /** Upsert an operator's state (availability / active call). */
  setOperator(op: OperatorState): void {
    this.operators.set(op.operatorId, op);
  }

  removeOperator(operatorId: string): void {
    this.operators.delete(operatorId);
  }

  /** The longest-waiting client (FIFO) — the next an operator should pick up. */
  peekNextClient(): QueuedClient | undefined {
    return this.waiting[0];
  }

  /** The next 'active' (available) operator to route a call to, if any. */
  nextAvailableOperator(): OperatorState | undefined {
    for (const op of this.operators.values()) if (op.availability === 'active') return op;
    return undefined;
  }

  /** True when every known operator is busy/offline — so a new caller must queue. */
  allOperatorsBusy(): boolean {
    const ops = [...this.operators.values()];
    return ops.length > 0 && ops.every((o) => o.availability !== 'active');
  }

  snapshot(): CallQueueSnapshot {
    const operators = [...this.operators.values()];
    return {
      roomId: this.roomId,
      waiting: [...this.waiting],
      operators,
      availableOperators: operators.filter((o) => o.availability === 'active').length,
      busyOperators: operators.filter((o) => o.availability === 'busy').length,
    };
  }

  clear(): void {
    this.waiting = [];
    this.operators.clear();
  }
}
